import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import { Theme } from '../../utils/theme';

interface TypingIndicatorProps {
  dotColor?: string;
  size?: number;
}

const TypingIndicator: React.FC<TypingIndicatorProps> = ({ 
  dotColor = Theme.colors.textSecondary,
  size = 8
}) => {
  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const waveAnimation = Animated.loop(
      Animated.sequence([
        // Animate all three dots simultaneously with staggered start
        Animated.parallel([
          Animated.sequence([
            Animated.timing(dot1, {
              toValue: 1,
              duration: 400,
              useNativeDriver: true,
            }),
            Animated.timing(dot1, {
              toValue: 0,
              duration: 400,
              useNativeDriver: true,
            }),
          ]),
          Animated.sequence([
            Animated.delay(150),
            Animated.timing(dot2, {
              toValue: 1,
              duration: 400,
              useNativeDriver: true,
            }),
            Animated.timing(dot2, {
              toValue: 0,
              duration: 400,
              useNativeDriver: true,
            }),
          ]),
          Animated.sequence([
            Animated.delay(300),
            Animated.timing(dot3, {
              toValue: 1,
              duration: 400,
              useNativeDriver: true,
            }),
            Animated.timing(dot3, {
              toValue: 0,
              duration: 400,
              useNativeDriver: true,
            }),
          ]),
        ]),
        // Pause before next wave
        Animated.delay(600),
      ])
    );

    waveAnimation.start();

    return () => {
      waveAnimation.stop();
    };
  }, [dot1, dot2, dot3]);

  const animatedStyle = (dot: Animated.Value) => ({
    transform: [
      {
        translateY: dot.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -8],
        }),
      },
    ],
    opacity: dot.interpolate({
      inputRange: [0, 1],
      outputRange: [0.4, 1],
    }),
  });

  return (
    <View style={styles.wrapper}>
      <View style={styles.container}>
        <Animated.View
          style={[
            styles.dot,
            { width: size, height: size, backgroundColor: dotColor },
            animatedStyle(dot1),
          ]}
        />
        <Animated.View
          style={[
            styles.dot,
            { width: size, height: size, backgroundColor: dotColor },
            animatedStyle(dot2),
          ]}
        />
        <Animated.View
          style={[
            styles.dot,
            { width: size, height: size, backgroundColor: dotColor },
            animatedStyle(dot3),
          ]}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    alignSelf: 'flex-start',
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingTop: 14,
    paddingBottom: 8,
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.lg,
    borderWidth: 0.3,
    borderColor: 'rgba(0, 0, 0, 0.08)',
  },
  dot: {
    borderRadius: 999,
  },
});

export default TypingIndicator;

